<template>
  <label
    :aria-label="labelText"
    :class="['input', { 'input--safari': isSafari }]"
  >
    <div class="input__details">
      <div v-if="showLabel" class="input__label">
        {{ labelText }}
      </div>
      <div v-if="maxLength" class="input__limit">
        {{ maxLengthText }}
      </div>
    </div>
    <div
      :class="[
        'input__field',
        { 'input__field--focused': isFocused },
        { 'input__field--disabled': isDisabled },
        { 'input__field--error': errorMessage },
      ]"
    >
      <SearchIcon v-if="isSearch" class="input__field-search-icon" />

      <input
        v-bind="$attrs"
        v-model="inputValue"
        :class="['input__field-input', { 'has-date': hasDate }]"
        :maxlength="maxLength"
        :aria-invalid="errorMessage ? 'true' : undefined"
        @focus="focus"
        @blur="blur"
        @change="handleChange"
        @input="handleInput"
      />
      <!-- The message below already says what is wrong; the mark only flags where -->
      <img
        v-if="errorMessage"
        :src="errorXIcon"
        alt=""
        class="input__field-error-icon"
      />
      <ButtonClose
        v-if="showClearButton"
        size="tiny"
        tabindex="-1"
        :title="$t('buttons.CLEAR_SEARCH')"
        @click="clearValue"
      />
      <BaseButtonIcon
        v-if="ai"
        theme="purple"
        rounded
        size="tiny"
        aria-label="AI Suggestions"
        title="AI Suggestions"
        @click="clickAiButton"
      >
        <AiIcon />
      </BaseButtonIcon>
    </div>
    <div
      v-if="message"
      :class="['input__message', { 'input__message--error': errorMessage }]"
    >
      {{ message }}
    </div>
  </label>
</template>

<script lang="ts">
import { errorXIcon } from '@/assets/img'
import AiIcon from '@/components/icon_components/AiIcon.vue'
import SearchIcon from '@/components/icon_components/SearchIcon.vue'
import { isSafari } from '@/helpers/userAgent'
import { debounce } from 'lodash-es'
import ButtonClose from '../ButtonClose.vue'
import BaseButtonIcon from './BaseButtonIcon.vue'

export default {
  components: {
    AiIcon,
    BaseButtonIcon,
    ButtonClose,
    SearchIcon,
  },
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: undefined,
    },
    showLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
    optional: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorMessage: {
      type: String,
      required: false,
      default: '',
    },
    supportingMessage: {
      type: String,
      required: false,
      default: '',
    },
    ai: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['change', 'input', 'onAi'],
  data() {
    return {
      isFocused: false,
      inputValue: this.value,
      isSafari,
      errorXIcon,
    }
  },
  computed: {
    message() {
      if (this.errorMessage) {
        return this.errorMessage
      } else if (this.supportingMessage) {
        return this.supportingMessage
      } else {
        return ''
      }
    },

    labelText() {
      const optionalText = this.optional ? `(${this.$t('OPTIONAL')})` : ''
      return `${this.label} ${optionalText}`
    },

    isSearch() {
      return this.$attrs.type === 'search'
    },

    isDisabled() {
      return this.$attrs.disabled
    },

    maxLength() {
      return this.$attrs.maxlength
    },

    showClearButton() {
      if (!this.inputValue) {
        return false
      }
      return this.isSearch && this.inputValue?.length > 0
    },

    maxLengthText() {
      const count = this.inputValue?.length || 0
      return `${count}/${this.maxLength}`
    },

    hasDate() {
      return this.inputValue && this.$attrs.type === 'date'
    },
  },

  watch: {
    value: {
      handler(newValue) {
        this.inputValue = newValue
      },
    },
  },

  methods: {
    handleChange() {
      this.$emit('change', this.inputValue)
    },

    // TODO: suggestion - debounce should probably be opt-in
    // There's many scenarios where we'd probably not want to debounce
    handleInput: debounce(function () {
      this.$emit('input', this.inputValue)
    }, 350),

    focus() {
      this.isFocused = true
    },

    blur() {
      this.isFocused = false
    },

    clearValue() {
      this.inputValue = undefined
      this.$emit('change', this.inputValue)
    },

    clickAiButton() {
      this.$emit('onAi')
    },
  },
}
</script>

<style lang="scss" scoped>
.input {
  &__details {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }

  &__label {
    @include formLabelStyle;
  }

  &__limit {
    @include formLabelStyle;
    font-weight: normal;
  }

  &__field {
    display: flex;
    align-items: center;
    padding: 0 $spacing-4xs;
    border: 1px solid $color-border;
    border-radius: $spacing-4xs;
    background: $color-canvas;
    outline: none;

    ::-webkit-search-decoration,
    ::-webkit-search-cancel-button,
    ::-webkit-search-results-button,
    ::-webkit-search-results-decoration {
      -webkit-appearance: none;
    }

    &--disabled {
      background: $color-disabled;
    }

    &--focused {
      outline: 2px solid $color-navigation;
    }

    &--error {
      border-color: $color-error;

      &.input__field--focused {
        outline-color: $color-error;
      }
    }
  }

  &__field-error-icon {
    flex-shrink: 0;
    width: $spacing-2xs;
    height: $spacing-2xs;
    margin: 0 $spacing-5xs;
  }

  &__field-search-icon {
    width: $spacing-2xs;
    height: $spacing-2xs;
    margin: 0 $spacing-5xs;
    color: $color-text-light;
  }

  &__field-input {
    width: 100%;
    padding: $spacing-4xs $spacing-5xs;
    line-height: $spacing-xs;
    background: none;
    border: none;
    outline: none;
    line-height: $spacing-xs;

    &[type='date'] {
      &::-webkit-datetime-edit-day-field:focus,
      &::-webkit-datetime-edit-month-field:focus,
      &::-webkit-datetime-edit-year-field:focus {
        background-color: #b4d5fe;
      }
    }
  }

  &__message {
    margin-top: $spacing-5xs;
    font-size: $font-size-s;
    color: $color-text-light;

    &--error {
      color: $color-canvas;
      background: $color-error;
      padding: $spacing-4xs;
      border-radius: $spacing-5xs;
    }
  }

  &--safari {
    .input__field-input {
      &[type='date'] {
        color: $color-ash;
      }

      &[type='date'].has-date {
        color: $color-text;
      }
    }
  }
}
</style>
